import React, { useEffect, useRef, useState } from "react";
import { X, Check, Undo2, Trash2, Pencil, MoveUpRight, Circle } from "lucide-react";
import { Button } from "@/components/ui/button";

const COLORS = ["#ef4444", "#f59e0b", "#22c55e", "#3b82f6", "#ffffff", "#111111"];
const TOOLS = [
  { id: "pen", label: "Draw", icon: Pencil },
  { id: "arrow", label: "Arrow", icon: MoveUpRight },
  { id: "circle", label: "Circle", icon: Circle },
];

/**
 * Lets a worker circle / arrow / scribble on the exact problem spot of a
 * pending image (photo or grabbed video frame) before it goes to the AI.
 * On save the drawing is flattened onto the image at full resolution and
 * returned as a fresh JPEG data URL — nothing is uploaded here.
 */
export function ImageMarkup({ src, onSave, onClose, T = (x) => x }) {
  const canvasRef = useRef(null);
  const baseRef = useRef(null); // loaded Image
  const shapesRef = useRef([]); // committed shapes
  const drawingRef = useRef(null); // shape in progress
  const [tool, setTool] = useState("circle");
  const [color, setColor] = useState("#ef4444");
  const [ready, setReady] = useState(false);
  const [, force] = useState(0);
  const toolRef = useRef(tool);
  const colorRef = useRef(color);
  toolRef.current = tool;
  colorRef.current = color;

  useEffect(() => {
    const img = new Image();
    img.onload = () => {
      baseRef.current = img;
      const canvas = canvasRef.current;
      canvas.width = img.naturalWidth || 1024;
      canvas.height = img.naturalHeight || 768;
      setReady(true);
      redraw();
    };
    img.src = src;
  }, [src]);

  const lineWidth = () => Math.max(3, Math.round((canvasRef.current?.width || 1024) / 220));

  const drawShape = (ctx, s) => {
    ctx.strokeStyle = s.color;
    ctx.lineWidth = s.width;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    if (s.type === "pen") {
      ctx.beginPath();
      s.points.forEach((p, i) => (i ? ctx.lineTo(p.x, p.y) : ctx.moveTo(p.x, p.y)));
      ctx.stroke();
    } else if (s.type === "circle") {
      const cx = (s.start.x + s.end.x) / 2;
      const cy = (s.start.y + s.end.y) / 2;
      const dx = s.end.x - s.start.x;
      const dy = s.end.y - s.start.y;
      const r = Math.max(1, Math.hypot(dx, dy) / 2); // always a round circle
      ctx.beginPath();
      ctx.arc(cx, cy, r, 0, Math.PI * 2);
      ctx.stroke();
    } else if (s.type === "arrow") {
      const { start: a, end: b } = s;
      ctx.beginPath();
      ctx.moveTo(a.x, a.y);
      ctx.lineTo(b.x, b.y);
      ctx.stroke();
      const ang = Math.atan2(b.y - a.y, b.x - a.x);
      const head = s.width * 4;
      ctx.beginPath();
      ctx.moveTo(b.x, b.y);
      ctx.lineTo(b.x - head * Math.cos(ang - Math.PI / 6), b.y - head * Math.sin(ang - Math.PI / 6));
      ctx.moveTo(b.x, b.y);
      ctx.lineTo(b.x - head * Math.cos(ang + Math.PI / 6), b.y - head * Math.sin(ang + Math.PI / 6));
      ctx.stroke();
    }
  };

  const redraw = () => {
    const canvas = canvasRef.current;
    if (!canvas || !baseRef.current) return;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(baseRef.current, 0, 0, canvas.width, canvas.height);
    shapesRef.current.forEach((s) => drawShape(ctx, s));
    if (drawingRef.current) drawShape(ctx, drawingRef.current);
  };

  const toImgCoords = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * canvas.width;
    const y = ((e.clientY - rect.top) / rect.height) * canvas.height;
    return { x, y };
  };

  const onPointerDown = (e) => {
    if (!ready) return;
    e.currentTarget.setPointerCapture?.(e.pointerId);
    const p = toImgCoords(e);
    const w = lineWidth();
    const t = toolRef.current;
    drawingRef.current =
      t === "pen"
        ? { type: "pen", color: colorRef.current, width: w, points: [p] }
        : { type: t, color: colorRef.current, width: w, start: p, end: p };
    redraw();
  };
  const onPointerMove = (e) => {
    if (!drawingRef.current) return;
    const p = toImgCoords(e);
    if (drawingRef.current.type === "pen") drawingRef.current.points.push(p);
    else drawingRef.current.end = p;
    redraw();
  };
  const onPointerUp = () => {
    if (!drawingRef.current) return;
    shapesRef.current = [...shapesRef.current, drawingRef.current];
    drawingRef.current = null;
    redraw();
    force((n) => n + 1);
  };

  const undo = () => {
    shapesRef.current = shapesRef.current.slice(0, -1);
    redraw();
    force((n) => n + 1);
  };
  const clearAll = () => {
    shapesRef.current = [];
    redraw();
    force((n) => n + 1);
  };
  const save = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    onSave(canvas.toDataURL("image/jpeg", 0.85));
  };

  return (
    <div className="image-markup-safe-panel fixed inset-0 z-[90] bg-black/90 flex flex-col overflow-hidden" data-testid="image-markup-panel">
      <div className="shrink-0 flex items-center justify-between text-white mb-2 gap-2 flex-wrap" data-testid="image-markup-toolbar">
        <div className="flex items-center gap-1.5 max-w-full overflow-x-auto scrollbar-none pb-0.5" data-testid="image-markup-tools-colors">
          {TOOLS.map((tl) => (
            <button
              key={tl.id}
              type="button"
              onClick={() => setTool(tl.id)}
              className={`h-11 w-11 shrink-0 rounded-sm border flex items-center justify-center transition ${tool === tl.id ? "bg-white text-black border-white" : "border-white/30 hover:bg-white/10"}`}
              title={T(tl.label)}
              data-testid={`image-markup-tool-${tl.id}`}
            >
              <tl.icon className="w-4 h-4" />
            </button>
          ))}
          <div className="w-px h-6 bg-white/20 mx-1" />
          {COLORS.map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => setColor(c)}
              className={`h-7 w-7 rounded-full border-2 transition ${color === c ? "border-white scale-110" : "border-white/30"}`}
              style={{ backgroundColor: c }}
              title={c}
              data-testid={`image-markup-color-${c.replace("#", "")}`}
            />
          ))}
        </div>
        <div className="flex items-center gap-1.5">
          <button type="button" onClick={undo} className="h-11 w-11 rounded-sm border border-white/30 text-white flex items-center justify-center hover:bg-white/10" title={T("Undo")} data-testid="image-markup-undo">
            <Undo2 className="w-4 h-4" />
          </button>
          <button type="button" onClick={clearAll} className="h-11 w-11 rounded-sm border border-white/30 text-white flex items-center justify-center hover:bg-white/10" title={T("Clear")} data-testid="image-markup-clear">
            <Trash2 className="w-4 h-4" />
          </button>
          <button type="button" onClick={onClose} className="h-11 w-11 rounded-sm border border-white/30 text-white flex items-center justify-center hover:bg-white/10" title={T("Cancel")} data-testid="image-markup-cancel">
            <X className="w-4 h-4" />
          </button>
          <Button type="button" onClick={save} className="rounded-sm h-11" data-testid="image-markup-save">
            <Check className="w-4 h-4 mr-2" /> {T("Done")}
          </Button>
        </div>
      </div>
      <div className="flex-1 min-h-0 flex items-center justify-center">
        <canvas
          ref={canvasRef}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerLeave={onPointerUp}
          className="max-w-full max-h-full object-contain rounded-sm shadow-2xl touch-none cursor-crosshair bg-black"
          data-testid="image-markup-canvas"
        />
      </div>
      <p className="shrink-0 text-center text-white/70 text-xs mt-2" data-testid="image-markup-help">
        {T("Circle or arrow the problem spot, then tap Done.")}
      </p>
    </div>
  );
}
